import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { UserFormEnhanced } from './LoanOfficerProfileFormEnhanced';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { useAuth } from '@/contexts/useAuth';
import { hasProfileAdminRole } from '@/services/seoApprovalService';
import { LwThemeScope } from '@/components/lw';

const log = createComponentLogger('UnifiedUserProfile', 'user-management');

const normalizeRoles = (roles: unknown): string[] =>
  Array.isArray(roles)
    ? roles
        .filter((role): role is string => typeof role === 'string')
        .map(role => role.trim().toLowerCase())
        .filter(Boolean)
    : [];

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  roles: string[];
  employment_status?: string;  // 'active', 'pending', 'terminated', 'inactive'
  organization_id: string;
}

export const UnifiedUserProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);

  // Load user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!userId) {
        navigate('/internal?section=manage-users');
        return;
      }

      if (!currentUser?.id) {
        return;
      }

      try {
        setLoading(true);

        const { data: actorProfile, error: actorError } = await supabase
          .from('users')
          .select('roles')
          .eq('id', currentUser.id)
          .single();

        if (actorError || !actorProfile) {
          log.error('Failed to load current user profile', { userId: currentUser.id }, actorError);
          toast.error('Failed to verify access');
          navigate('/internal?section=manage-users');
          return;
        }

        const actorRoles = normalizeRoles(actorProfile.roles);
        // Shared predicate: admin OR super_admin, matching the database
        // contract used by the SEO-approval RPCs and other admin-gated APIs.
        const actorIsAdmin = hasProfileAdminRole(actorRoles);
        const actorIsPartner = actorRoles.includes('partner');
        const isSelf = userId === currentUser.id;

        setIsAdmin(actorIsAdmin);
        setIsPartner(actorIsPartner);

        if (actorIsPartner && !actorIsAdmin && !isSelf) {
          const { data: canManage, error: partnerAccessError } = await supabase.rpc('can_partner_manage_user', {
            actor_id: currentUser.id,
            target_id: userId
          });

          if (partnerAccessError || canManage !== true) {
            log.warn('Partner attempted to open unmanaged user profile', {
              actorId: currentUser.id,
              targetUserId: userId,
              error: partnerAccessError
            });
            toast.error('You do not have access to manage this user');
            navigate('/internal?section=manage-users');
            return;
          }
        }
        
        // Load user basic info FROM users table
        const { data: profile, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          log.error('Failed to load user profile', { userId }, error);
          toast.error('Failed to load user profile');
          navigate('/internal?section=manage-users');
          return;
        }

        setUserProfile({
          id: profile.id,
          email: profile.email,
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          role: profile.roles?.[0] || 'user',
          roles: profile.roles || ['user'],
          employment_status: profile.employment_status ?? 'active',
          organization_id: profile.organization_id
        });
      } catch (error) {
        log.error('Error loading user profile', { userId }, error as Error);
        toast.error('Error loading user profile');
        navigate('/internal?section=manage-users');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
    // currentUser object identity churns on auth events (e.g. SIGNED_IN
    // re-broadcasts from same-origin preview iframes); only the id is read
    // here, so depend on it to avoid unmount/remount loops.
  }, [userId, currentUser?.id, navigate]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      // eslint-disable-next-line no-alert -- existing unsaved-changes flow uses a native confirm prompt
      if (!confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    navigate('/internal?section=manage-users');
  };

  const handleProfileUpdate = async () => {
    setHasUnsavedChanges(false);

    if (!userId) {
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error || !profile) {
        const refreshError = error ?? new Error('Updated profile query returned no row');
        log.error('Failed to refresh updated user profile', { userId }, refreshError);
        toast.error('Profile saved, but the updated summary could not be refreshed');
        return;
      }

      setUserProfile({
        id: profile.id,
        email: profile.email,
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        role: profile.roles?.[0] || 'user',
        roles: profile.roles || ['user'],
        employment_status: profile.employment_status ?? 'active',
        organization_id: profile.organization_id
      });
    } catch (error) {
      log.error('Error refreshing updated user profile', { userId }, error as Error);
      toast.error('Profile saved, but the updated summary could not be refreshed');
    }
  };

  // Determine user type and what tabs to show
  const isLoanOfficer = userProfile?.roles?.includes('loan_officer');
  const isSelfView = userId === currentUser?.id;
  const managementMode = isAdmin
    ? 'admin'
    : isPartner && !isSelfView
      ? 'partner_delegated'
      : 'self';

  if (loading) {
    return (
      <LwThemeScope className="lw-ds-page lw-user-profile">
        <div className="wrap">
          <section className="lw-user-profile__section lw-user-profile__state" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            <div>
              <h1>Loading user profile</h1>
              <p>Preparing contact, licensing, integration, and compensation details.</p>
            </div>
          </section>
        </div>
      </LwThemeScope>
    );
  }

  if (!userProfile) {
    return (
      <LwThemeScope className="lw-ds-page lw-user-profile">
        <div className="wrap">
          <section className="lw-user-profile__section lw-user-profile__state" role="alert">
            <div>
              <h1>User profile unavailable</h1>
              <p>The user could not be found or is no longer available.</p>
            </div>
            <Button onClick={() => navigate('/internal?section=manage-users')}>
              Back to users
            </Button>
          </section>
        </div>
      </LwThemeScope>
    );
  }

  return (
    <LwThemeScope className="lw-ds-page lw-user-profile">
      <div className="wrap">
        <nav className="lw-user-profile__breadcrumb" aria-label="Breadcrumb">
          <Button variant="ghost" size="sm" onClick={handleBack} className="lw-user-profile__back">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to users
          </Button>
          <span aria-hidden="true">›</span>
          <span>Company Settings</span>
          <span aria-hidden="true">›</span>
          <span>Manage Users</span>
          <span aria-hidden="true">›</span>
          <span className="lw-user-profile__breadcrumb-current" aria-current="page">
            {userProfile.first_name} {userProfile.last_name}
          </span>
        </nav>

        <UserFormEnhanced
          userId={userId}
          onSuccess={handleProfileUpdate}
          isAdminView={isAdmin}
          managementMode={managementMode}
          showCompensation={isLoanOfficer && isAdmin}
          showSelfCompensation={isLoanOfficer && isSelfView && managementMode === 'self'}
          showBranchSelection={isAdmin}
        />
      </div>
    </LwThemeScope>
  );
};
